import { useFeatureFlag } from '@src/hooks';
import { useCustomers, useCustomersByName, useCustomersContext } from '@src/hooks/useCustomer';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import { baseRestApi, createTag } from '@src/utils/rtkQuery';
import { EMPTY_ARRAY } from '@talos/kyoko';
import { useCallback, useMemo, useRef } from 'react';
import {
  createCustomerValidationIndex,
  getCreateCustomerRequest,
  type CreateCustomerValidationIndex,
} from '../Dialogs/CreateCustomerDrawer.utils';
import { useCustomerTierOptions } from '../Dialogs/useCustomerTierOptions';
import {
  CUSTOMER_CONFIGURATIONS_COLUMNS,
  getCreateCustomerDrawerOptions,
  getEditCustomerDrawerOptions,
  getEntityForPatchPost,
  type CustomerConfigurationDrawerRecord,
  type CustomerConfigurationWithDisplayName,
} from './utils';

interface UseCustomerConfigurationsEntityAdminConfigProps {
  enableDrawerOptions?: boolean;
}

export function useCustomerConfigurationsEntityAdminConfig({
  enableDrawerOptions = true,
}: UseCustomerConfigurationsEntityAdminConfigProps = {}) {
  const { showCustomerTiering } = useFeatureFlag();
  const dispatch = useAppStateDispatch();
  const customers = useCustomers();
  const customersByName = useCustomersByName();
  const { createCustomer, updateCustomer } = useCustomersContext();
  const tierOptions = useCustomerTierOptions(enableDrawerOptions && showCustomerTiering);
  // Track pending DisplayName updates until customersByName is refreshed
  const pendingDisplayNamesRef = useRef<Map<string, string>>(new Map());

  const customerValidationIndex = useMemo<CreateCustomerValidationIndex | undefined>(
    () => (customers == null ? undefined : createCustomerValidationIndex(customers)),
    [customers]
  );

  const createDrawerOptions = useMemo(() => {
    if (!enableDrawerOptions || customerValidationIndex == null || (showCustomerTiering && tierOptions == null)) {
      return undefined;
    }

    return getCreateCustomerDrawerOptions({
      customerValidationIndex,
      showCustomerTiering,
      allInTierOptions: tierOptions?.allInTiers,
      feeTierOptions: tierOptions?.feeTiers,
      symbolGroupOptions: tierOptions?.symbolGroups,
    });
  }, [customerValidationIndex, enableDrawerOptions, showCustomerTiering, tierOptions]);

  const editDrawerOptions = useMemo(() => {
    if (!enableDrawerOptions || (showCustomerTiering && tierOptions == null)) {
      return undefined;
    }

    return getEditCustomerDrawerOptions({
      showCustomerTiering,
      allInTierOptions: tierOptions?.allInTiers ?? EMPTY_ARRAY,
      feeTierOptions: tierOptions?.feeTiers ?? EMPTY_ARRAY,
      symbolGroupOptions: tierOptions?.symbolGroups ?? EMPTY_ARRAY,
    });
  }, [enableDrawerOptions, showCustomerTiering, tierOptions]);

  const columns = useMemo(() => {
    if (showCustomerTiering) {
      return CUSTOMER_CONFIGURATIONS_COLUMNS;
    }

    return CUSTOMER_CONFIGURATIONS_COLUMNS.filter(column => {
      const field = 'field' in column ? column.field : undefined;
      return field && !['AllInTier', 'FeeTier', 'SymbolGroup'].includes(field);
    });
  }, [showCustomerTiering]);

  const getDisplayName = useCallback(
    ({ Counterparty }: { Counterparty: string }): string => {
      return (
        pendingDisplayNamesRef.current.get(Counterparty) ??
        customersByName?.get(Counterparty)?.DisplayName ??
        Counterparty
      );
    },
    [customersByName]
  );

  const saveCustomerDisplayName = useCallback(
    async (entity: CustomerConfigurationWithDisplayName) => {
      const originalDisplayName = getDisplayName(entity);
      if (entity.DisplayName !== originalDisplayName && entity.DisplayName !== undefined) {
        await updateCustomer({ Name: entity.Counterparty, DisplayName: entity.DisplayName });
      }
      pendingDisplayNamesRef.current.set(entity.Counterparty, entity.DisplayName ?? '');
    },
    [getDisplayName, updateCustomer]
  );

  const getCustomerConfigurationForPatchPost = useCallback(
    (entity: CustomerConfigurationWithDisplayName) => {
      pendingDisplayNamesRef.current.set(entity.Counterparty, entity.DisplayName ?? '');
      return getEntityForPatchPost(entity, customersByName, updateCustomer);
    },
    [customersByName, updateCustomer]
  );

  const createCustomerFromDrawerRecord = useCallback(
    (entity: CustomerConfigurationDrawerRecord) => {
      const request = getCreateCustomerRequest({
        name: entity.Name ?? '',
        displayName: entity.DisplayName ?? '',
        allInTier: entity.AllInTier,
        feeTier: entity.FeeTier,
        symbolGroup: entity.SymbolGroup,
      });

      return createCustomer(request);
    },
    [createCustomer]
  );

  const invalidateCustomerConfigurationQueries = useCallback(() => {
    dispatch(baseRestApi.util.invalidateTags([createTag('CustomerConfiguration', 'LIST')]));
  }, [dispatch]);

  const serverDataToRecordMappingFunc = useCallback(
    (data: unknown) => {
      const entity = data as CustomerConfigurationWithDisplayName;
      return {
        ...entity,
        DisplayName: entity.DisplayName ?? getDisplayName(entity),
      };
    },
    [getDisplayName]
  );

  return {
    columns,
    createCustomerFromDrawerRecord,
    createDrawerOptions,
    customersByName,
    editDrawerOptions,
    getCustomerConfigurationForPatchPost,
    getDisplayName,
    saveCustomerDisplayName,
    invalidateCustomerConfigurationQueries,
    serverDataToRecordMappingFunc,
  };
}
